import { useEffect, useRef, useState } from 'react';

const STOCK_FILTER_OPTIONS = [
  { id: null, label: 'All Stock', dotClass: 'bg-gray-400' },
  { id: 'zero', label: 'Zero Stock', dotClass: 'bg-red-600', activeClass: 'bg-red-50 text-red-800 border border-red-200' },
  { id: 'low', label: 'Low (1-5)', dotClass: 'bg-red-500', activeClass: 'bg-red-50 text-red-800 border border-red-200' },
  { id: 'medium', label: 'Medium (40-60%)', dotClass: 'bg-orange-500', activeClass: 'bg-amber-50 text-amber-800 border border-amber-200' },
  { id: 'full', label: 'Full (60%+)', dotClass: 'bg-green-600', activeClass: 'bg-green-50 text-green-800 border border-green-200' },
];

const SearchIcon = () => (
  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg
    className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const InventoryToolbar = ({
  searchQuery,
  onSearchChange,
  categories = [],
  selectedCategory,
  onCategoryChange,
  selectedStockFilter,
  onStockFilterChange,
  collapsibleStockFilters = false,
  onAddNew,
  onClearAllFilters,
  resultCount,
  totalCount,
}) => {
  const searchRef = useRef(null);
  const [stockExpanded, setStockExpanded] = useState(Boolean(selectedStockFilter));

  useEffect(() => {
    if (selectedStockFilter) {
      setStockExpanded(true);
    }
  }, [selectedStockFilter]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      const tag = event.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || event.target?.isContentEditable) {
        return;
      }
      event.preventDefault();
      searchRef.current?.focus();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const activeFilterCount =
    (searchQuery.trim() ? 1 : 0) +
    (selectedCategory ? 1 : 0) +
    (selectedStockFilter ? 1 : 0);

  const handleClearAllFilters = () => {
    onSearchChange('');
    onCategoryChange?.(null);
    onStockFilterChange?.(null);
    onClearAllFilters?.();
    setStockExpanded(false);
  };

  const renderCategoryChips = () => {
    if (categories.length === 0) return null;

    return (
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs text-gray-500 shrink-0">Size:</span>
        <div className="filter-scroll min-w-0 flex-1">
          <button
            type="button"
            onClick={() => onCategoryChange?.(null)}
            className={`filter-chip ${selectedCategory === null ? 'filter-chip-active' : ''}`}
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => onCategoryChange?.(category.id)}
              className={`filter-chip ${selectedCategory === category.id ? 'filter-chip-active' : ''}`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderStockChips = () => {
    if (!onStockFilterChange) return null;

    const chips = (
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs text-gray-500 shrink-0">Stock:</span>
        <div className="filter-scroll min-w-0 flex-1">
          {STOCK_FILTER_OPTIONS.map((option) => {
            const isActive = selectedStockFilter === option.id;
            const chipClass = isActive
              ? option.activeClass
                ? `filter-chip ${option.activeClass}`
                : 'filter-chip filter-chip-active'
              : 'filter-chip';
            return (
              <button
                key={option.id ?? 'all-stock'}
                type="button"
                onClick={() => onStockFilterChange(option.id)}
                className={chipClass}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${option.dotClass}`} aria-hidden="true" />
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    );

    if (!collapsibleStockFilters) {
      return chips;
    }

    return (
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setStockExpanded((prev) => !prev)}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ChevronIcon open={stockExpanded} />
          Stock filters
          {selectedStockFilter && (
            <span className="inline-flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold">
              1
            </span>
          )}
        </button>
        {stockExpanded && chips}
      </div>
    );
  };

  return (
    <div className="compact-toolbar">
      {/* Search + actions row */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <div className="relative flex-1 min-w-0 max-w-full sm:max-w-md">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
            <SearchIcon />
          </span>
          <input
            ref={searchRef}
            type="text"
            placeholder="Search models…  (press /)"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="toolbar-input"
            aria-label="Search inventory"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none w-5 h-5 flex items-center justify-center rounded"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onAddNew}
            className="toolbar-btn-primary"
          >
            + Add Model
          </button>
        </div>
      </div>

      {/* Category chips */}
      {categories.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t border-gray-100">
          {renderCategoryChips()}
        </div>
      )}

      {/* Stock chips (gallery) */}
      {onStockFilterChange && (
        <div className={`${categories.length > 0 ? 'mt-2' : 'mt-2.5 pt-2.5 border-t border-gray-100'}`}>
          {renderStockChips()}
        </div>
      )}

      {/* Result count + active filter summary */}
      {(resultCount != null || activeFilterCount > 0) && (
        <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500">
            {resultCount != null && (
              totalCount != null && resultCount !== totalCount
                ? `Showing ${resultCount} of ${totalCount} items`
                : `${resultCount} item${resultCount !== 1 ? 's' : ''}`
            )}
            {resultCount != null && activeFilterCount > 0 && ' · '}
            {activeFilterCount > 0 && `${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''} active`}
          </span>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={handleClearAllFilters}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors shrink-0"
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default InventoryToolbar;
